import type { ImpactType } from "@s2z-platform/constants";
import { impactTypes } from "@s2z-platform/constants";

// ─── Types ───────────────────────────────────────────────────────────────────

export type ImpactFormatLabels = {
  typeName: string;
  typeNameLower: string;
  typeNameSingular: string;
  quantityWithUnit: string;
  quantityWithStorageUnit: string;
  unitType: string;
  storageUnitType: string;
  unit: string;
  smallestUnit: string;
  displayName: string;
  displayNameCap: string;
  displayNameLower: string;
  displayNameUnit: string;
  displayNameUnitCap: string;
  displayNameUnitShort: string;
  action: string;
  actionSimple: string;
  purchaseCta: string;
  noImpact: string;
  noImpactPeriod: string;
  noImpactProducts: string;
  verb: string;
  doing: string;
};

export type ImpactDisplayFormatConfig = {
  divisor: number;
};

// ─── Config ──────────────────────────────────────────────────────────────────

export const PLASTIC_G_TO_KG_DIVISOR = 1000;
export const PLASTIC_G_TO_BOTTLES_DIVISOR = 20;
export const CO2E_KG_TO_TONNES_DIVISOR = 1000;
export const TREES_WHOLE_TO_SINGLE_DIVISOR = 1;

export const IMPACT_DISPLAY_FORMATS = {
  plastic_kg: {
    divisor: PLASTIC_G_TO_KG_DIVISOR,
  },
  plastic_bottles: {
    divisor: PLASTIC_G_TO_BOTTLES_DIVISOR,
  },
  co2e_tonnes: {
    divisor: CO2E_KG_TO_TONNES_DIVISOR,
  },
  trees_single: {
    divisor: TREES_WHOLE_TO_SINGLE_DIVISOR,
  },
} as const satisfies Record<string, ImpactDisplayFormatConfig>;

export type ImpactDisplayFormatKey = keyof typeof IMPACT_DISPLAY_FORMATS;

// ─── Default Format Maps ─────────────────────────────────────────────────────

export const DEFAULT_FORMAT_MAP: Record<ImpactType, ImpactDisplayFormatKey> = {
  trees: "trees_single",
  co2e: "co2e_tonnes",
  plastic: "plastic_kg",
};

export const WIDGET_DEFAULT_FORMAT_MAP: Record<
  ImpactType,
  ImpactDisplayFormatKey
> = {
  trees: "trees_single",
  co2e: "co2e_tonnes",
  plastic: "plastic_kg",
};

// ─── Runtime Assertion ───────────────────────────────────────────────────────

for (const [key, config] of Object.entries(IMPACT_DISPLAY_FORMATS)) {
  if (config.divisor <= 0) {
    throw new Error(
      `IMPACT_DISPLAY_FORMATS["${key}"].divisor must be > 0, got ${config.divisor}`,
    );
  }
}

// ─── Format Functions ─────────────────────────────────────────────────────────

export function getFormatKey(
  type: ImpactType,
  options?: { format?: ImpactDisplayFormatKey },
): ImpactDisplayFormatKey {
  if (options?.format) {
    return options.format;
  }
  return DEFAULT_FORMAT_MAP[type];
}

// ─── Format Impact Function ──────────────────────────────────────────────────

export function formatImpact(
  type: ImpactType,
  storedValue: number,
  options?: { format?: ImpactDisplayFormatKey },
): {
  value: number;
  formatKey: ImpactDisplayFormatKey;
} {
  const resolvedFormatKey: ImpactDisplayFormatKey =
    options?.format ?? DEFAULT_FORMAT_MAP[type];
  const config = IMPACT_DISPLAY_FORMATS[resolvedFormatKey];

  if (storedValue === 0 || !Number.isFinite(storedValue)) {
    return { value: 0, formatKey: resolvedFormatKey };
  }

  const value = storedValue / config.divisor;
  return { value, formatKey: resolvedFormatKey };
}

// ─── Existing Functions ──────────────────────────────────────────────────────

export function getAllImpactTypesExcept(impactsToExclude: ImpactType[]) {
  return impactTypes.filter((iT) => !impactsToExclude.includes(iT));
}

const IMPACT_DISPLAY_ORDER: ImpactType[] = ["trees", "co2e", "plastic"];

export function orderImpacts(impactsArr: ImpactType[]): ImpactType[];
export function orderImpacts<T extends { type: ImpactType }>(
  impactsArr: T[],
): T[];
export function orderImpacts<T extends ImpactType | { type: ImpactType }>(
  impactsArr: T[],
): T[] {
  if (!impactsArr.length) return impactsArr;
  if (typeof impactsArr[0] === "string")
    return [...IMPACT_DISPLAY_ORDER] as T[];
  return ([...impactsArr] as ({ type: ImpactType } & T)[]).sort(
    (a, b) =>
      IMPACT_DISPLAY_ORDER.indexOf(a.type) -
      IMPACT_DISPLAY_ORDER.indexOf(b.type),
  ) as T[];
}
